import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Home, CalendarDays, Users, AlertTriangle, MoreHorizontal } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { setTabBarHidden, useTabBarHidden } from '@/hooks/useTabBarVisibility';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Tunables
const SCROLL_THRESHOLD = 12; // px of direction travel before we toggle
const KEYBOARD_THRESHOLD = 200; // px drop in visualViewport height that signals keyboard

const MobileTabBar = ({ onMorePress }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Initialise from cached value so the tab set is stable on cold load
  // (prevents the width-shift flicker when the admin visibility API resolves).
  const [crisisVisible, setCrisisVisible] = useState(() => {
    try {
      return localStorage.getItem('rollm8s_crisis_visible') === 'true';
    } catch {
      return false;
    }
  });
  const hidden = useTabBarHidden();

  // New events today — drives the small badge dot on the Events tab so
  // the app feels alive and pulls users in the moment something fresh
  // lands. Uses created_at (last 24h) — matches the daily IG draft
  // window so the two signals stay in sync.
  const [newEventsToday, setNewEventsToday] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/events?upcoming_only=true&limit=50`)
      .then(res => {
        if (cancelled) return;
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const count = (res.data || []).filter(ev => {
          if (!ev.created_at) return false;
          const t = new Date(ev.created_at).getTime();
          return !Number.isNaN(t) && t >= cutoff;
        }).length;
        setNewEventsToday(count);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname]);

  // Load crisis zone visibility config and cache it for next cold load
  useEffect(() => {
    axios.get(`${API}/admin/crisis-zone-visibility`)
      .then(res => {
        const v = !!res.data.crisis_zone_visible;
        setCrisisVisible(v);
        try { localStorage.setItem('rollm8s_crisis_visible', v ? 'true' : 'false'); } catch {}
      })
      .catch(() => {});
  }, []);

  // Always show on route change so navigation is discoverable
  useEffect(() => {
    setTabBarHidden(false);
  }, [pathname]);

  // Scroll direction detector — hide when scrolling down, show when scrolling up.
  // Always visible when at the top of the page.
  const lastY = useRef(0);
  const accum = useRef(0);
  useEffect(() => {
    lastY.current = window.scrollY || 0;
    const onScroll = () => {
      const y = window.scrollY || 0;
      // Always show at the very top
      if (y < 16) {
        accum.current = 0;
        setTabBarHidden(false);
        lastY.current = y;
        return;
      }
      const dy = y - lastY.current;
      // Accumulate small movements in the same direction, reset on reversal
      if ((dy > 0 && accum.current < 0) || (dy < 0 && accum.current > 0)) accum.current = 0;
      accum.current += dy;
      if (accum.current > SCROLL_THRESHOLD) {
        setTabBarHidden(true);
        accum.current = 0;
      } else if (accum.current < -SCROLL_THRESHOLD) {
        setTabBarHidden(false);
        accum.current = 0;
      }
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Keyboard detection — hide when the on-screen keyboard shrinks the viewport.
  // Uses visualViewport (supported on Chromium/Samsung Internet/Android WebView).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const baseline = window.innerHeight;
    const onResize = () => {
      const shrunk = baseline - vv.height > KEYBOARD_THRESHOLD;
      if (shrunk) setTabBarHidden(true);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  const tabs = [
    { key: 'home', icon: Home, label: 'Home', path: user ? '/dashboard' : '/' },
    { key: 'events', icon: CalendarDays, label: 'Events', path: '/events' },
    { key: 'community', icon: Users, label: 'Community', path: '/rollflix' },
    ...(crisisVisible ? [{ key: 'crisis', icon: AlertTriangle, label: 'Blackout', path: '/blackout' }] : []),
    { key: 'more', icon: MoreHorizontal, label: 'More', action: 'menu' },
  ];

  const isActive = (tab) => {
    if (tab.action) return false;
    if (tab.path === '/') return pathname === '/';
    return pathname.startsWith(tab.path);
  };

  // Hide the tab bar entirely on focused communication surfaces — having it
  // hide/show on scroll inside chat caused a feedback loop with the input bar's
  // sticky-bottom positioning (page height changed on every show/hide, forcing
  // the body to auto-correct scrollY, which re-triggered the scroll listener,
  // which toggled the tab bar again — observed 463 scroll events in 15s of
  // idle time before this guard was added).
  const isFocusedSurface = pathname?.startsWith('/chat/') || pathname?.startsWith('/messages/');

  // Expose the tab bar's rendered height as a CSS variable so pages can pad
  // their bottom content above it. Updated on resize (e.g. keyboard open) so
  // callers always have an accurate value. When the bar is suppressed (focused
  // surface or app-level hide), the variable is forced to 0px.
  const tabBarRef = useRef(null);
  useEffect(() => {
    const measure = () => {
      const h = (!isFocusedSurface && tabBarRef.current)
        ? tabBarRef.current.getBoundingClientRect().height
        : 0;
      try {
        document.documentElement.style.setProperty('--mobile-tab-bar-h', (hidden || isFocusedSurface) ? '0px' : `${h}px`);
      } catch { /* ignore */ }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [hidden, isFocusedSurface]);

  if (isFocusedSurface) return null;

  return (
    <nav
      ref={tabBarRef}
      className={`md:hidden fixed bottom-0 left-0 right-0 z-[9999] border-t border-white/5 backdrop-blur-xl transition-transform duration-200 ease-out will-change-transform ${
        hidden ? 'translate-y-full' : 'translate-y-0'
      }`}
      data-testid="mobile-tab-bar"
      data-hidden={hidden ? 'true' : 'false'}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Top 56px (icon row) = 20% theme primary (very see-through glass).
        // Bottom safe-area sliver = SOLID theme primary, so it matches the
        // solid Samsung OS nav bar below seamlessly.
        background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.2) 0, hsl(var(--primary) / 0.2) 56px, hsl(var(--primary)) 56px, hsl(var(--primary)) 100%)',
      }}
    >
      <div className="flex items-center justify-around h-14 px-1">
        {tabs.map((tab) => {
          const active = isActive(tab);
          const Icon = tab.icon;
          const showEventsBadge = tab.key === 'events' && newEventsToday > 0;
          return (
            <button
              key={tab.key}
              onClick={() => tab.action === 'menu' ? onMorePress?.() : navigate(tab.path)}
              className={`relative flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors ${
                active ? 'text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.6)]' : 'text-white/70'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                {showEventsBadge && (
                  <span
                    data-testid="tab-events-badge"
                    className="absolute -top-1.5 -right-3 h-[18px] px-1.5 rounded-full bg-pink-500 text-white text-[9px] font-bold leading-[18px] text-center ring-2 ring-[hsl(var(--primary))] shadow-[0_0_8px_rgba(236,72,153,0.7)] whitespace-nowrap"
                    aria-label={`${newEventsToday} new event${newEventsToday === 1 ? '' : 's'} today`}
                  >
                    {newEventsToday > 9 ? '9+' : newEventsToday} new
                  </span>
                )}
              </div>
              <span className={`text-[10px] leading-none ${active ? 'font-bold' : 'font-medium'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileTabBar;
